import styled from "styled-components";

export const Page = styled.main`
  min-height: 100vh;
  background: var(--global-bg-color);
  color: var(--global-text-color);
`;

export const Nav = styled.header`
  border-bottom: 1px solid var(--global-divider-color);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(180%) blur(12px);
  position: sticky;
  top: 0;
  z-index: 20;
`;

export const NavInner = styled.div`
  max-width: 1120px;
  min-height: 56px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

export const Brand = styled.div`
  font-family: "Source Serif 4", Georgia, serif;
  font-size: 2.1rem;
  font-weight: 600;
  letter-spacing: 0;
`;

export const NavMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  color: var(--global-text-color-light);
  font-size: 1.45rem;
  white-space: nowrap;
`;

export const Shell = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 34px 24px 42px;
`;

export const HeadingRow = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: end;
  margin-bottom: 24px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

export const TitleBlock = styled.div`
  h1 {
    margin: 0;
    font-family: "Source Serif 4", Georgia, serif;
    font-size: clamp(3.4rem, 5vw, 5.6rem);
    line-height: 1;
    font-weight: 650;
    letter-spacing: 0;
  }

  p {
    margin-top: 12px;
    max-width: 660px;
    color: var(--global-text-color-light);
    font-size: 1.62rem;
    line-height: 1.58;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(92px, 1fr));
  gap: 1px;
  border: 1px solid var(--global-divider-color);
  background: var(--global-divider-color);
  min-width: 360px;

  @media (max-width: 760px) {
    min-width: 0;
    width: 100%;
  }
`;

export const Stat = styled.div`
  background: var(--global-card-bg-color);
  padding: 14px 16px;

  strong {
    display: block;
    font-family: "Source Serif 4", Georgia, serif;
    font-size: 2.8rem;
    line-height: 1;
    font-weight: 650;
    color: var(--global-theme-color);
  }

  span {
    display: block;
    margin-top: 6px;
    color: var(--global-text-color-light);
    font-size: 1.28rem;
  }
`;

export const Workspace = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const MapPanel = styled.div`
  border-top: 1px solid var(--global-divider-color);
  padding-top: 18px;
`;

export const MapToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: "Source Serif 4", Georgia, serif;
  font-size: 2.2rem;
  line-height: 1.2;
  font-weight: 600;
`;

export const TextButton = styled.button`
  appearance: none;
  border: 1px solid var(--global-divider-color);
  background: var(--global-card-bg-color);
  color: var(--global-text-color);
  padding: 7px 12px;
  font: inherit;
  font-size: 1.36rem;
  cursor: pointer;

  &:hover {
    border-color: var(--global-theme-color);
    color: var(--global-theme-color);
  }
`;

export const MapCanvas = styled.div`
  height: min(68vh, 640px);
  min-height: 460px;
  border: 1px solid var(--global-divider-color);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 760px) {
    min-height: 390px;
    height: 56vh;
  }
`;

export const SidePanel = styled.aside`
  border-top: 1px solid var(--global-divider-color);
  padding-top: 18px;
`;

export const SearchBox = styled.div`
  position: relative;
  margin-top: 14px;
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid var(--global-divider-color);
  border-radius: 0;
  padding: 0 12px;
  background: var(--global-card-bg-color);
  color: var(--global-text-color);
  font: inherit;
  font-size: 1.52rem;
  outline: none;

  &:focus {
    border-color: var(--global-theme-color);
    box-shadow: 0 0 0 3px rgba(0, 118, 223, 0.12);
  }
`;

export const SuggestionList = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  border: 1px solid var(--global-divider-color);
  max-height: 288px;
  overflow: auto;
  background: var(--global-card-bg-color);
`;

export const SuggestionItem = styled.li`
  button {
    width: 100%;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 14px;
    padding: 10px 12px;
    background: transparent;
    color: var(--global-text-color);
    cursor: pointer;
    text-align: left;
    font: inherit;
    border-bottom: 1px solid var(--global-divider-color);
  }

  &:last-child button {
    border-bottom: 0;
  }

  button:hover {
    background: var(--global-code-bg-color);
  }

  strong {
    font-size: 1.5rem;
    font-weight: 600;
  }

  span {
    color: var(--global-text-color-light);
    font-size: 1.26rem;
    white-space: nowrap;
  }
`;

export const ManualAction = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px dashed var(--global-divider-color);
  background: transparent;
  color: var(--global-theme-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
`;

export const VisitedList = styled.div`
  margin-top: 26px;
`;

export const CityList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
`;

export const CityRow = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  border-bottom: 1px solid var(--global-divider-color);
  padding: 9px 0;

  strong {
    display: block;
    font-size: 1.5rem;
    font-weight: 600;
  }

  span {
    display: block;
    margin-top: 2px;
    color: var(--global-text-color-light);
    font-size: 1.24rem;
  }

  button {
    color: var(--global-text-color-light);
    background: transparent;
    border: 0;
    font: inherit;
    cursor: pointer;
    padding: 6px;
  }

  button:hover {
    color: var(--global-highlight-color);
  }
`;

export const EmptyState = styled.p`
  margin: 12px 0 0;
  color: var(--global-text-color-light);
  font-size: 1.44rem;
  line-height: 1.55;
`;
